import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Sun, Cloud, CloudRain, CloudFog, CloudSnow, Droplets, Wind,
  Fuel, Flame, Wind as WindIcon, Moon, Sunrise, Sunset, Sparkles, MessageCircle
} from "lucide-react";

const CODE_MAP = {
  0: { l: "साफ़", Icon: Sun },
  1: { l: "अधिकतर साफ़", Icon: Sun },
  2: { l: "आंशिक बादल", Icon: Cloud },
  3: { l: "बादल", Icon: Cloud },
  45: { l: "कोहरा", Icon: CloudFog },
  48: { l: "कोहरा", Icon: CloudFog },
  51: { l: "बूंदाबांदी", Icon: CloudRain },
  61: { l: "हल्की बारिश", Icon: CloudRain },
  63: { l: "बारिश", Icon: CloudRain },
  65: { l: "भारी बारिश", Icon: CloudRain },
  71: { l: "बर्फ़", Icon: CloudSnow },
  80: { l: "बौछारें", Icon: CloudRain },
  95: { l: "आंधी-तूफ़ान", Icon: CloudRain },
};

// Weekday in Hindi
const DAY_HI = ["रविवार", "सोमवार", "मंगलवार", "बुधवार", "गुरुवार", "शुक्रवार", "शनिवार"];
const MONTH_HI = ["जनवरी","फ़रवरी","मार्च","अप्रैल","मई","जून","जुलाई","अगस्त","सितम्बर","अक्टूबर","नवम्बर","दिसम्बर"];

function AqiTrendMiniChart({ series }) {
  const values = series.map((s) => s.aqi);
  const min = Math.min(...values), max = Math.max(...values);
  const range = max - min || 1;
  const w = 200, h = 30;
  const stepX = w / (values.length - 1);
  const pts = values.map((v, i) => `${(i * stepX).toFixed(1)},${(h - ((v - min) / range) * (h - 4) - 2).toFixed(1)}`).join(" ");
  const last = series[series.length - 1];
  return (
    <div className="mt-2 pt-2 border-t border-slate-200/60" data-testid="brief-aqi-trend">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">24 घंटे का ट्रेंड</span>
        <span className="text-[10px] text-slate-600">↓ {min} · ↑ {max}</span>
      </div>
      <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="overflow-visible">
        <defs>
          <linearGradient id="aqi-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#EF4444" stopOpacity="0.28"/>
            <stop offset="100%" stopColor="#EF4444" stopOpacity="0"/>
          </linearGradient>
        </defs>
        <polygon fill="url(#aqi-grad)" points={`0,${h} ${pts} ${w},${h}`}/>
        <polyline fill="none" stroke="#0F172A" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" points={pts}/>
        <circle cx={((values.length - 1) * stepX).toFixed(1)} cy={(h - ((last.aqi - min) / range) * (h - 4) - 2).toFixed(1)} r="2.6" fill="#0F172A"/>
      </svg>
    </div>
  );
}

export default function MorningBriefingCard() {
  const [w, setW] = useState(null);
  const [aqi, setAqi] = useState(null);
  const [fp, setFp] = useState(null);
  const [p, setP] = useState(null);

  useEffect(() => {
    api.get("/weather").then((r) => setW(r.data)).catch(() => {});
    api.get("/aqi").then((r) => setAqi(r.data)).catch(() => {});
    api.get("/fuel-prices").then((r) => setFp(r.data)).catch(() => {});
    api.get("/panchang/today").then((r) => setP(r.data)).catch(() => {});
  }, []);

  const today = new Date();
  const dateLabel = `${DAY_HI[today.getDay()]}, ${today.getDate()} ${MONTH_HI[today.getMonth()]} ${today.getFullYear()}`;

  const wCode = w?.current?.weather_code;
  const wInfo = CODE_MAP[wCode] || { l: "—", Icon: Cloud };
  const WIcon = wInfo.Icon;
  const temp = w?.current?.temperature_2m != null ? Math.round(w.current.temperature_2m) : null;
  const humid = w?.current?.relative_humidity_2m;
  const wind = w?.current?.wind_speed_10m != null ? Math.round(w.current.wind_speed_10m) : null;
  const tmax = w?.daily?.temperature_2m_max?.[0];
  const tmin = w?.daily?.temperature_2m_min?.[0];

  const petrol = fp?.petrol;
  const diesel = fp?.diesel;
  const cng = fp?.cng;

  // Build a compact WhatsApp-forwardable text summary of the briefing
  const buildShareText = () => {
    const lines = [];
    lines.push("*INDIAN-NEWS-21 · आज की एक-नज़र रिपोर्ट*");
    lines.push(`_${dateLabel}_`);
    if (p?.tithi) lines.push(`🕉 तिथि: ${p.tithi}${p.hindu_month ? ` · ${p.hindu_month}` : ""}`);
    lines.push("");
    if (temp != null) {
      const parts = [`${temp}° ${wInfo.l}`];
      if (tmax != null) parts.push(`(↑${Math.round(tmax)}°/↓${Math.round(tmin)}°)`);
      if (humid != null) parts.push(`नमी ${humid}%`);
      lines.push(`🌤 मौसम: ${parts.join(" ")}`);
    }
    if (aqi?.aqi != null) {
      lines.push(`💨 AQI: ${aqi.aqi} · ${aqi.level}${aqi.advice ? ` — ${aqi.advice}` : ""}`);
    }
    if (petrol != null || diesel != null || cng != null) {
      const f = [];
      if (petrol != null) f.push(`पेट्रोल ₹${Number(petrol).toFixed(2)}`);
      if (diesel != null) f.push(`डीज़ल ₹${Number(diesel).toFixed(2)}`);
      if (cng != null) f.push(`CNG ₹${Number(cng).toFixed(2)}`);
      lines.push(`⛽ ईंधन (${fp?.city || "सूरजपुर"}): ${f.join(" · ")}`);
    }
    if (p?.sunrise || p?.sunset) {
      lines.push(`🌅 सूर्योदय: ${p.sunrise || "—"} · सूर्यास्त: ${p.sunset || "—"}`);
    }
    if (p?.rahu_kaal) lines.push(`⚠️ राहुकाल: ${p.rahu_kaal}`);
    lines.push("");
    lines.push("रोज़ की ख़बरें: " + (typeof window !== "undefined" ? window.location.origin : ""));
    lines.push("— प्रधान संपादक JAKIR MOMIN");
    return lines.join("\n");
  };

  const shareWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(buildShareText())}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <section className="mb-8 rounded-2xl overflow-hidden shadow-xl border border-slate-200/70 bg-white" data-testid="morning-briefing">
      {/* Header ribbon */}
      <div className="bg-gradient-to-r from-[#0F172A] via-[#1E3A8A] to-[#0F172A] px-5 md:px-6 py-3 flex items-center gap-3 flex-wrap relative overflow-hidden">
        <div className="absolute -top-6 -right-6 opacity-10 text-[100px] font-devnagri-heading font-black leading-none pointer-events-none select-none text-amber-300">21</div>
        <Sunrise className="w-5 h-5 text-amber-400 relative"/>
        <div className="relative">
          <div className="uppercase tracking-[0.28em] text-[10px] font-black text-amber-300">Morning Briefing</div>
          <div className="font-devnagri-heading text-white text-base md:text-lg font-black leading-none mt-0.5">आज की एक-नज़र रिपोर्ट</div>
        </div>
        <div className="ml-auto flex items-center gap-3 relative">
          <div className="text-right hidden sm:block">
            <div className="text-[10px] uppercase tracking-widest text-slate-400">{dateLabel}</div>
            {p?.tithi && <div className="text-[11px] text-amber-200 font-bold">{p.tithi} · {p.hindu_month || ""}</div>}
          </div>
          <button onClick={shareWhatsApp} title="WhatsApp पर पूरा ब्रीफ़िंग भेजें"
            className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-white text-[11px] font-bold px-3 py-1.5 rounded-full transition shadow-md"
            data-testid="briefing-share-wa">
            <MessageCircle className="w-3.5 h-3.5"/>
            <span className="hidden sm:inline">WhatsApp पर भेजें</span>
            <span className="sm:hidden">भेजें</span>
          </button>
        </div>
      </div>

      {/* Main 4-tile grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-slate-100">
        {/* Weather */}
        <div className="p-4 md:p-5 bg-gradient-to-br from-sky-50 to-blue-50" data-testid="brief-weather">
          <div className="flex items-center gap-2 mb-2">
            <WIcon className="w-4 h-4 text-sky-600"/>
            <span className="uppercase tracking-widest text-[10px] font-bold text-sky-800">मौसम</span>
          </div>
          {temp != null ? (
            <>
              <div className="flex items-baseline gap-1.5">
                <span className="font-devnagri-heading font-black text-3xl text-[#0F172A] leading-none">{temp}°</span>
                <span className="text-sm text-slate-600 font-medium">{wInfo.l}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-600">
                {tmax != null && <span>↑ {Math.round(tmax)}° / ↓ {Math.round(tmin)}°</span>}
                {humid != null && <span className="inline-flex items-center gap-1"><Droplets className="w-3 h-3"/>{humid}%</span>}
                {wind != null && <span className="inline-flex items-center gap-1"><Wind className="w-3 h-3"/>{wind} km/h</span>}
              </div>
              <div className="mt-2 text-[10px] text-slate-500 truncate">{w?.location || "सूरजपुर"}</div>
            </>
          ) : <div className="text-xs text-slate-400 italic">डेटा लोड हो रहा है…</div>}
        </div>

        {/* AQI */}
        <div className="p-4 md:p-5" style={{ background: aqi?.color ? `linear-gradient(135deg, ${aqi.color}15, ${aqi.color}05)` : undefined }} data-testid="brief-aqi">
          <div className="flex items-center gap-2 mb-2">
            <WindIcon className="w-4 h-4" style={{ color: aqi?.color || "#6B7280" }}/>
            <span className="uppercase tracking-widest text-[10px] font-bold" style={{ color: aqi?.color || "#6B7280" }}>वायु गुणवत्ता · AQI</span>
          </div>
          {aqi?.aqi != null ? (
            <>
              <div className="flex items-baseline gap-1.5">
                <span className="font-devnagri-heading font-black text-3xl leading-none" style={{ color: aqi.color }}>{aqi.aqi}</span>
                <span className="text-sm font-bold" style={{ color: aqi.color }}>{aqi.level}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-x-3 text-[11px] text-slate-600">
                {aqi.pm2_5 != null && <span>PM2.5 <b>{Math.round(aqi.pm2_5)}</b></span>}
                {aqi.pm10 != null && <span>PM10 <b>{Math.round(aqi.pm10)}</b></span>}
              </div>
              <div className="mt-2 text-[10.5px] text-slate-600 leading-snug line-clamp-2">{aqi.advice}</div>
              {/* 3-day forecast pills */}
              {aqi.forecast?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-200/60 flex items-center gap-1.5" data-testid="brief-aqi-forecast">
                  <span className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">3 दिन:</span>
                  {aqi.forecast.slice(0, 3).map((f, i) => {
                    const d = new Date(f.date);
                    const label = i === 0 ? "आज" : (i === 1 ? "कल" : new Date(f.date).toLocaleDateString("hi-IN", { weekday: "short" }));
                    return (
                      <div key={f.date} className="flex-1 text-center rounded-md px-1 py-1 leading-tight" style={{ background: `${f.color}18` }} title={`औसत AQI ${f.avg} — ${f.level}`}>
                        <div className="text-[9px] text-slate-600 font-bold">{label}</div>
                        <div className="text-[11px] font-black" style={{ color: f.color }}>{f.avg}</div>
                      </div>
                    );
                  })}
                </div>
              )}
              {/* 24-hour AQI mini chart */}
              {aqi.hourly_24h?.length >= 3 && (
                <AqiTrendMiniChart series={aqi.hourly_24h}/>
              )}
            </>
          ) : <div className="text-xs text-slate-400 italic">डेटा लोड हो रहा है…</div>}
        </div>

        {/* Fuel */}
        <div className="p-4 md:p-5 bg-gradient-to-br from-rose-50 to-orange-50" data-testid="brief-fuel">
          <div className="flex items-center gap-2 mb-2">
            <Fuel className="w-4 h-4 text-rose-600"/>
            <span className="uppercase tracking-widest text-[10px] font-bold text-rose-800">ईंधन दाम</span>
          </div>
          {petrol != null || diesel != null || cng != null ? (
            <div className="space-y-1">
              {petrol != null && (
                <div className="flex items-baseline gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 w-14">पेट्रोल</span>
                  <span className="font-devnagri-heading font-black text-lg text-rose-700 leading-none">₹{Number(petrol).toFixed(2)}</span>
                </div>
              )}
              {diesel != null && (
                <div className="flex items-baseline gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 w-14">डीज़ल</span>
                  <span className="font-devnagri-heading font-black text-lg text-emerald-700 leading-none">₹{Number(diesel).toFixed(2)}</span>
                </div>
              )}
              {cng != null && (
                <div className="flex items-baseline gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 w-14">CNG</span>
                  <span className="font-devnagri-heading font-black text-lg text-sky-700 leading-none">₹{Number(cng).toFixed(2)}</span>
                </div>
              )}
              <div className="mt-1.5 text-[10px] text-slate-500 truncate">{fp?.city || "स्थानीय दाम"}</div>
            </div>
          ) : <div className="text-xs text-slate-400 italic">एडमिन ने अभी दाम नहीं डाले</div>}
        </div>

        {/* Panchang / Muhurat */}
        <div className="p-4 md:p-5 bg-gradient-to-br from-amber-50 to-yellow-50" data-testid="brief-panchang">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-amber-600"/>
            <span className="uppercase tracking-widest text-[10px] font-bold text-amber-800">पंचांग</span>
          </div>
          {p ? (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-[12.5px]">
                <Moon className="w-3.5 h-3.5 text-amber-700 shrink-0"/>
                <span className="text-slate-500 w-14 text-[10px] uppercase tracking-wider">नक्षत्र</span>
                <b className="text-[#0F172A] truncate">{p.nakshatra}</b>
              </div>
              <div className="flex items-center gap-2 text-[12.5px]">
                <Sunrise className="w-3.5 h-3.5 text-amber-700 shrink-0"/>
                <span className="text-slate-500 w-14 text-[10px] uppercase tracking-wider">सूर्योदय</span>
                <b className="text-[#0F172A]">{p.sunrise}</b>
              </div>
              <div className="flex items-center gap-2 text-[12.5px]">
                <Sunset className="w-3.5 h-3.5 text-amber-700 shrink-0"/>
                <span className="text-slate-500 w-14 text-[10px] uppercase tracking-wider">सूर्यास्त</span>
                <b className="text-[#0F172A]">{p.sunset}</b>
              </div>
              {p.rahu_kaal && <div className="mt-1 text-[10.5px] bg-red-50 border border-red-100 text-red-700 rounded px-2 py-1 truncate"><b>राहुकाल:</b> {p.rahu_kaal}</div>}
            </div>
          ) : <div className="text-xs text-slate-400 italic">डेटा लोड हो रहा है…</div>}
        </div>
      </div>
    </section>
  );
}
